/**
 * Utilitaires POS réutilisables (frontend)
 * Basé sur les utils du backend et de FoliesPOS
 */

// TVA Belgique: 0% (exonéré), 6% (alimentaire), 21% (standard)
export type TaxClass = 'STANDARD_21' | 'ALIM_6' | 'ZERO_0';

export interface Product {
  id: string;
  code?: string | null;
  name: string;
  barcode?: string | null;
  is_weighted?: boolean;
  unit?: string | null;
  tax_class?: TaxClass | string | null;
  image_url?: string | null;
  description?: string | null;
  is_deposit?: boolean;
  deposit_qty_per_unit?: number | null;
  deposit_product_id?: string | null;
  active?: boolean;
  stock_quantity?: number;
}

export interface CartLine {
  product: Product | null;
  product_name?: string;
  note?: string;
  qty: number;
  unit_price_cents: number;
  tax_rate?: number;
  is_deposit?: boolean;
  is_return?: boolean;
  is_custom?: boolean;
  is_refund?: boolean;
  is_gift_voucher?: boolean;
  lot_id?: string;
  lot_promo_percent?: number;
  original_price_cents?: number;
}

/**
 * Retourne le taux de TVA selon la classe fiscale
 */
export function vatRateOf(taxClass?: TaxClass | string | null): number {
  // Support des formats string comme "0%", "6%", "21%"
  // TVA Belgique: 0% (exonéré/vidanges), 6% (alimentaire), 21% (standard)
  if (typeof taxClass === 'string') {
    if (taxClass === '0%' || taxClass === 'EXEMPT' || taxClass === 'VIDANGE') return 0.0;
    if (taxClass === '6%') return 0.06;
    if (taxClass === '21%') return 0.21;
  }

  switch (taxClass) {
    case 'STANDARD_21':
      return 0.21;
    case 'ALIM_6':
      return 0.06;
    case 'ZERO_0':
      return 0.0;
    default:
      return 0.21;
  }
}

/**
 * Sépare un montant TTC en HT et TVA
 */
export function splitTTC(ttcCents: number, rate: number): { ht: number; vat: number } {
  const ht = Math.round(ttcCents / (1 + rate));
  const vat = ttcCents - ht;
  return { ht, vat };
}

/**
 * Calcule le montant TTC depuis HT
 */
export function calculateTTC(htCents: number, rate: number): number {
  return Math.round(htCents * (1 + rate));
}

/**
 * Formate un montant en centimes en euros avec symbole
 */
export function formatPrice(cents: number): string {
  return (cents / 100).toFixed(2).replace('.', ',') + ' €';
}

/**
 * Formate un montant en centimes en euros (sans symbole)
 */
export function formatEuros(cents: number): string {
  return (cents / 100).toFixed(2);
}

/**
 * Formate une quantité / un poids sans bruit flottant.
 * Arrondit à 3 décimales max (précision au gramme) et retire les zéros superflus.
 * Tolère number, string (Decimal Prisma sérialisé), null/undefined.
 * Ex : 16.769000000001 → "16.769", 9 → "9", 0.5 → "0.5"
 */
export function formatQty(value: number | string | null | undefined): string {
  const n = typeof value === 'string' ? parseFloat(value) : value ?? 0;
  if (n == null || !isFinite(n)) return '0';
  return String(parseFloat(n.toFixed(3)));
}

/**
 * Arrondit à 2 décimales
 */
export function round2(num: number): number {
  return Math.round(num * 100) / 100;
}

/**
 * Calcule les totaux d'une vente depuis les lignes
 */
export function calculateSaleTotals(lines: CartLine[]): {
  subtotal_ht_cents: number;
  tax_total_cents: number;
  total_ttc_cents: number;
  subtotal_ht_euros: string;
  tax_total_euros: string;
  total_ttc_euros: string;
} {
  let subtotal_ht_cents = 0;
  let tax_total_cents = 0;
  let total_ttc_cents = 0;

  for (const line of lines) {
    const qty = line.is_refund ? -Math.abs(line.qty) : line.qty;
    // Arrondir au cent près pour les produits pesés (cohérence avec stockage DB en integer)
    const line_ttc = Math.round(line.unit_price_cents * qty);
    const rate = line.tax_rate ?? (line.product ? vatRateOf(line.product.tax_class) : 0.21);
    const { ht, vat } = splitTTC(line_ttc, rate);

    subtotal_ht_cents += ht;
    tax_total_cents += vat;
    total_ttc_cents += line_ttc;
  }

  return {
    subtotal_ht_cents,
    tax_total_cents,
    total_ttc_cents,
    subtotal_ht_euros: formatEuros(subtotal_ht_cents),
    tax_total_euros: formatEuros(tax_total_cents),
    total_ttc_euros: formatEuros(total_ttc_cents),
  };
}

/**
 * Debounce function
 */
export function debounce<T extends (...args: any[]) => any>(fn: T, ms = 400): (...args: Parameters<T>) => void {
  let timeout: ReturnType<typeof setTimeout> | undefined;
  return (...args: Parameters<T>) => {
    if (timeout) clearTimeout(timeout);
    timeout = setTimeout(() => fn(...args), ms);
  };
}

// ============================================================================
// CONSTRUCTION DES LIGNES DE VENTE (source unique de vérité)
// ============================================================================

/**
 * Interface pour une ligne de vente prête à envoyer à l'API
 */
export interface SaleLineDto {
  product_id?: string;
  product_name: string;
  quantity: number;
  unit_price_cents: number;
  tax_rate: number;
  is_deposit: boolean;
  is_return: boolean;
  is_custom?: boolean;
  notes?: string;
  is_gift_voucher?: boolean;
  lot_id?: string;
  lot_promo_percent?: number;
  original_price_cents?: number;
}

/**
 * Construit les lignes de vente avec le tax_rate calculé correctement.
 * C'est LA source unique de vérité pour la conversion cart -> sale lines.
 *
 * @param cart - Les lignes du panier
 * @returns Les lignes formatées pour l'API avec tax_rate calculé
 */
export function buildSaleLines(cart: CartLine[]): SaleLineDto[] {
  // Même forme que la file hors-ligne (`POSContent` → `SalesQueue`) : prix
  // POSITIF, signe porté par la quantité. Un retour de consigne arrive au panier
  // avec un prix NÉGATIF (`DepositReturnDialog`) ; envoyé tel quel, le serveur le
  // refusait (`unit_price_cents` ≥ 0). Ce chemin étant celui du paiement FOLIES,
  // une consigne ne pouvait pas être rendue sur les FOLIES du client (31/10/2026).
  return cart.map((line) => ({
    product_id: line.product?.id || undefined,
    product_name: line.product_name || line.product?.name || 'Article',
    quantity: line.is_refund || line.is_return ? -Math.abs(line.qty) : line.qty,
    unit_price_cents: Math.abs(line.unit_price_cents),
    tax_rate: line.tax_rate ?? (line.product ? vatRateOf(line.product.tax_class) : 0.21),
    is_deposit: line.is_deposit || false,
    is_return: line.is_return || line.is_refund || false,
    is_custom: line.is_custom || false,
    notes: line.note || undefined,
    is_gift_voucher: line.is_gift_voucher || false,
    // Remise anti-gaspi par lot : le serveur décompte ce lot et trace la remise.
    ...(line.lot_id ? {
      lot_id: line.lot_id,
      lot_promo_percent: line.lot_promo_percent,
      original_price_cents: line.original_price_cents,
    } : {}),
  }));
}

/**
 * Calcule les totaux depuis les lignes de vente (version DTO)
 */
export function calculateSaleLinesTotals(lines: SaleLineDto[]): {
  total_cents: number;
  tax_total_cents: number;
} {
  let total_cents = 0;
  let tax_total_cents = 0;

  for (const line of lines) {
    // Arrondi au cent PAR LIGNE, comme le fait l'écran de caisse
    // (`calculateSaleTotals`) et comme le fait le serveur
    // (`calculateLineTotal`). Sans lui, 0,386 kg à 4,90 €/kg valait 189,14
    // centimes, et le ticket remis au client affichait un total qui ne
    // correspondait ni à ses propres lignes, ni au montant réellement encaissé.
    const lineTotalCents = Math.round(line.unit_price_cents * Math.abs(line.quantity));
    const taxCents = Math.round(lineTotalCents * line.tax_rate / (1 + line.tax_rate));

    // Pour les retours, le total est négatif
    const signedTotal = line.is_return ? -lineTotalCents : lineTotalCents;
    const signedTax = line.is_return ? -taxCents : taxCents;

    total_cents += signedTotal;
    tax_total_cents += signedTax;
  }

  return { total_cents, tax_total_cents };
}
